import { Button, Stack, TextField } from "@mui/material";
import { useFormik } from "formik";
import { omit } from "lodash-es";
import { useRef } from "react";

import ApiEnumAutocomplete from "@/common/components/Enum/ApiEnumAutocomplete";
import GeneralValidationError from "@/common/components/Error/GeneralValidationError";
import TenantIdentifierInput from "@/common/components/Form/Input/TenantIdentifierInput";
import AppModal, { AppModalProps } from "@/common/components/Modals/AppModal";
import AppModalActions from "@/common/components/Modals/AppModalActions";
import AppModalContent from "@/common/components/Modals/AppModalContent";
import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ApiEnumName } from "@/common/services/enum";
import { tenantService } from "@/common/services/tenant";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { appCommonConfig } from "@/config/config";
import { apiClient } from "@/core/api/ApiClient";
import { CreateCompanyDto } from "@/core/api/generated/v1";

interface Props extends AppModalProps {
  onCompanyCreated: () => void;
}

export default function TenantCreateModal({
  trigger,
  open,
  onOpen,
  onClose,
  onCompanyCreated,
}: Props) {
  const isIdentifierEditedRef = useRef(false);
  const { enqueueSnackbar } = useAppSnackbar();
  const mounted = useMounted();

  const formik = useFormik<BaseFormikValues & CreateCompanyDto>({
    enableReinitialize: true,
    initialValues: {
      name: undefined,
      identifier: undefined,
      type: undefined,
      description: undefined,
      department: undefined,
      submit: "",
    },
    onSubmit: async (values, { setFieldError, setSubmitting, setStatus }) => {
      setSubmitting(true);
      try {
        await apiClient.accountApi.apiV1AccountRegisterCompanyNewPost({
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          createCompanyDto: {
            ...omit(values, "submit"),
            name: values.name,
            identifier: values.identifier,
            type: values.type,
            description: values.description,
            department: values.department,
          },
        });
        enqueueSnackbar("New company created", { variant: "success" });
        onCompanyCreated();
        onClose!();
        if (mounted.current) {
          setStatus({ success: true });
          setSubmitting(false);
        }
      } catch (err: Error | any) {
        if (mounted.current) {
          ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
          setStatus({ success: false });
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldError,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = formik;

  // const brandingFormikProps = FormikHelper.getSubProps<
  //   BaseFormikValues & CreateCompanyDto,
  //   GeneralBrandingInputDto | undefined
  // >(formik, "department.branding", (v) => v.department?.branding);

  return (
    <AppModal
      fullWidth
      keepMounted={false}
      trigger={trigger}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    >
      {(params) => (
        <>
          <AppModalTitle title='Create new company' withClose onClose={params.close} />

          <AppModalContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name}
                fullWidth
                required
                autoComplete='off'
                label='Company name'
                name='name'
                value={values.name || ""}
                onBlur={handleBlur}
                onChange={(e) => {
                  setFieldValue("name", e.target.value);
                  if (!isIdentifierEditedRef.current) {
                    setFieldValue(
                      "identifier",
                      tenantService.formatNameAsTenantIdentifier(e.target.value),
                    );
                  }
                }}
              />

              <TenantIdentifierInput
                error={Boolean(errors.identifier)}
                helperText={errors.identifier}
                fullWidth
                required
                value={values.identifier || ""}
                onChange={async (e) => {
                  handleChange(e);
                  isIdentifierEditedRef.current = true;
                }}
                onError={(err) => {
                  const validation = ValidationHelper.handleApiErrorResponseFormik(
                    err,
                    setFieldError,
                  );
                  setFieldError("identifier", validation.getFieldError("identifier"));
                }}
              />

              <ApiEnumAutocomplete
                type={ApiEnumName.TenantType}
                label={`I will primarily use ${appCommonConfig.appName} as:`}
                value={values.type}
                textFieldProps={{
                  name: "type",
                  error: Boolean(touched.type && errors.type),
                  helperText: touched.type && errors.type,
                }}
                onBlur={handleBlur}
                onChange={(newValue) => {
                  setFieldValue("type", newValue);
                }}
              />

              <TextField
                error={Boolean(touched.description && errors.description)}
                helperText={touched.description && errors.description}
                fullWidth
                multiline
                rows={3}
                label='Description'
                name='description'
                value={values.description || ""}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Stack>

            <GeneralValidationError sx={{ my: 1 }} errors={errors} />

            <AppModalActions sx={{ mt: 2 }} modalProps={undefined} {...params.actionProps}>
              <Button
                variant='outlined'
                color='text'
                onClick={() => params.close(undefined, "childClose")}
              >
                Cancel
              </Button>
              <Button variant='contained' loading={isSubmitting} onClick={() => handleSubmit()}>
                Save
              </Button>
            </AppModalActions>
          </AppModalContent>
        </>
      )}
    </AppModal>
  );
}
